import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { CreateViewDrawer } from "../../../../app/newLayout/page/components/CreateView/CreateViewDrawer";
import { PortalFooter } from "../../../molecules/Footer/";
import { ColumnItem } from "../../TableSection/Detail/ActionBar/Modal/ColumnItem";
import {
  ColumnElement,
  ColumnInput,
  ColumnListWrapper,
  ElementHeading,
  StyledEditColumnsBody,
  StyledEditColumnsBodyLeft,
  StyledEditColumnsBodyRight,
  StyledEditColumnsShell,
} from "../../TableSection/Detail/ActionBar/Modal/Modal.styles";
import { useNotification } from "../../../../hooks/useNotification";
import {
  useMutateSummaryBarFieldsMutation,
  useResetSummaryBarFieldsMutation,
} from "../../../../store/apiSlice/authApiSlice";

const ResetLink = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  color: var(--DARK-BLUE, #1249bf);
  cursor: pointer;
  font-size: 12px;
  margin: 0 16px 8px;
  padding: 0;
  text-decoration: underline;

  &:disabled {
    color: var(--LIGHT-GREY, #8c8ca1);
    cursor: not-allowed;
    text-decoration: none;
  }
`;

export function EditSummaryBarFields({ isOpen, module, fields, selectedIds, onClose }) {
  const [mutateSummaryBarFields, { isLoading: isSaving }] = useMutateSummaryBarFieldsMutation();
  const [resetSummaryBarFields, { isLoading: isResetting }] = useResetSummaryBarFieldsMutation();
  const notify = useNotification();
  const dragIndexRef = useRef();

  const initialSelected = useMemo(
    () =>
      (selectedIds.length ? selectedIds : fields.map(field => field.id)).map(id => {
        const field = fields.find(item => item.id === id);
        return { id, header: field?.label ?? id };
      }),
    [fields, selectedIds],
  );

  const [selectedFields, setSelectedFields] = useState(initialSelected);

  useEffect(() => {
    if (isOpen) {
      setSelectedFields(initialSelected);
    }
  }, [initialSelected, isOpen]);

  const isLoading = isSaving || isResetting;
  const isDisabled = selectedFields.length < 1 || isLoading;
  const allSelected = selectedFields.length === fields.length;

  const handleSelectAll = useCallback(
    e => {
      if (e.target.checked) {
        setSelectedFields(fields.map(field => ({ id: field.id, header: field.label })));
      } else {
        setSelectedFields([]);
      }
    },
    [fields],
  );

  const handleFieldSelection = useCallback(
    e => {
      const { value, checked } = e.target;
      if (checked) {
        const toAdd = fields.find(field => field.id === value);
        if (toAdd) {
          setSelectedFields(prev => [...prev, { id: toAdd.id, header: toAdd.label }]);
        }
      } else {
        setSelectedFields(prev => prev.filter(item => item.id !== value));
      }
    },
    [fields],
  );

  const handleDragStart = useCallback((e, index) => {
    dragIndexRef.current = index;
  }, []);

  const handleDragOver = useCallback(e => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback((e, dropIndex) => {
    e.preventDefault();
    const dragIndex = dragIndexRef.current;
    if (dragIndex != null && dragIndex !== dropIndex) {
      setSelectedFields(prev => {
        const items = [...prev];
        const [moved] = items.splice(dragIndex, 1);
        items.splice(dropIndex, 0, moved);
        return items;
      });
    }
  }, []);

  const applyFields = async () => {
    try {
      await mutateSummaryBarFields({
        module,
        summary_bar_fields: selectedFields.map(item => item.id),
      }).unwrap();
      notify({ type: "success", message: "Summary fields updated successfully." });
      onClose();
    } catch {
      notify({ type: "error", message: "Failed to update summary fields." });
    }
  };

  const handleReset = async () => {
    try {
      await resetSummaryBarFields({
        module,
        summary_bar_fields: [],
      }).unwrap();
      notify({ type: "success", message: "Summary fields reset to default." });
      onClose();
    } catch {
      notify({ type: "error", message: "Failed to reset summary fields." });
    }
  };

  if (!isOpen) return null;

  return (
    <CreateViewDrawer isOpen title="Edit Summary Fields" onClose={onClose}>
      <StyledEditColumnsShell>
        <ResetLink type="button" onClick={handleReset} disabled={isLoading}>
          Reset to default
        </ResetLink>
        <StyledEditColumnsBody>
          <StyledEditColumnsBodyLeft>
            <ElementHeading>Available Fields</ElementHeading>
            <ColumnListWrapper>
              <ColumnElement key="selectAll">
                <ColumnInput
                  type="checkbox"
                  value="select-all"
                  checked={allSelected}
                  onChange={handleSelectAll}
                  aria-label="Select all fields"
                  disabled={isLoading}
                />
                SELECT ALL
              </ColumnElement>
              {fields.map(field => (
                <ColumnItem
                  key={field.id}
                  checked={selectedFields.some(item => item.id === field.id)}
                  disabled={isLoading}
                  draggable={false}
                  header={field.label}
                  id={field.id}
                  onChange={handleFieldSelection}
                />
              ))}
            </ColumnListWrapper>
          </StyledEditColumnsBodyLeft>

          <StyledEditColumnsBodyRight>
            <ElementHeading>Selected Fields</ElementHeading>
            <ColumnListWrapper>
              {selectedFields.map((field, idx) => (
                <ColumnItem
                  key={field.id}
                  checked={selectedFields.some(item => item.id === field.id)}
                  disabled={isLoading}
                  draggable
                  header={field.header}
                  id={field.id}
                  index={idx}
                  onChange={handleFieldSelection}
                  onDragOver={handleDragOver}
                  onDragStart={handleDragStart}
                  onDrop={handleDrop}
                />
              ))}
            </ColumnListWrapper>
          </StyledEditColumnsBodyRight>
        </StyledEditColumnsBody>
        <PortalFooter
          isDisabled={isDisabled}
          isLoading={isLoading}
          onApply={applyFields}
          onCancel={onClose}
        />
      </StyledEditColumnsShell>
    </CreateViewDrawer>
  );
}
